import React, { useState } from "react";
import {
  Box,
  Avatar,
  Typography,
  IconButton,
  TextField,
  Collapse,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ReplyIcon from "@mui/icons-material/Reply";
import SendIcon from "@mui/icons-material/Send";
import CommentIcon from "@mui/icons-material/Comment";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import DeleteIcon from "@mui/icons-material/Delete";
import { toggleLike } from "../../../services/likeService";
import { useDispatch } from "react-redux";
import { popupTimer } from "../../../consts";

const RateComment = ({ 
  comment, 
  level = 0, 
  onReply, 
  authorized,
  submitting,
  newReply,
  onReplyChange,
  onSubmitReply,
  onToggleReplies,
  expandedReplies,
  onDeleteComment,
  currentUser,
  isSpaceOwner,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(comment.likes || 0);
  const [isLiking, setIsLiking] = useState(false);
  const maxLevel = 2; // Maximum nesting level

  // Check if current user can delete this comment
  const currentUserName = currentUser?.user_name || currentUser?.username || currentUser?.name;
  const commentUserName = comment?.user_name || comment?.username;
  const isCommentOwner = currentUserName && commentUserName && (currentUserName === commentUserName);
  const userRole = currentUser?.role?.toLowerCase();
  const isAdmin = userRole === 'admin' || userRole === 'support';
  // Space owners can delete comments on rates in their collections
  const canDelete = isCommentOwner || isAdmin || isSpaceOwner;

  const handleReplyClick = () => {
    setShowReplyInput(true);
  };

  const handleSubmitReply = () => {
    if (!newReply[comment.id]?.trim() || !authorized) return;
    onSubmitReply(comment.id);
    setShowReplyInput(false);
  };

  const handleLike = async () => {
    if (!authorized || isLiking) return;
    
    setIsLiking(true);
    try {
      const response = await toggleLike({ comment_id: comment.id });
      setLikeCount(response.like_count || 0);
      setIsLiked(!isLiked);
    } catch (error) {
      if (error.message === "UNAUTHORIZED") {
        dispatch({ type: "SET_UNAUTHORIZED" });
      } else {
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: error?.response?.data?.error || "Failed to like. Please try again.",
            type: "error",
          },
        });
        setTimeout(() => {
          dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
        }, popupTimer);
      }
    } finally {
      setIsLiking(false);
    }
  };

  return (
    <Box 
      sx={{ 
        pl: level > 0 ? { xs: 2, sm: 3, md: 4 } : 0,
        mb: { xs: 1.5, sm: 2 },
        position: 'relative',
        '&::before': level > 0 ? {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '2px',
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
          borderRadius: '2px',
        } : {},
      }}
    >
      <Box 
        sx={{ 
          display: "flex", 
          alignItems: "flex-start", 
          gap: { xs: 1.5, sm: 2 },
          p: { xs: 1.5, sm: 2 },
          borderRadius: { xs: 1.5, sm: 2 },
          backgroundColor: 'background.paper',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: { xs: '0 1px 3px rgba(0, 0, 0, 0.05)', sm: '0 2px 8px rgba(0, 0, 0, 0.08)' },
          },
        }}
      >
        <Avatar
          src={comment.profile_image_url}
          alt={comment.username}
          sx={{ 
            width: { xs: 28, sm: 32 }, 
            height: { xs: 28, sm: 32 },
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            flexShrink: 0,
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ 
            display: "flex", 
            alignItems: "center", 
            mb: { xs: 0.75, sm: 1 },
            flexWrap: "wrap",
            gap: { xs: 0.5, sm: 0 },
          }}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 600,
                fontSize: { xs: "0.8125rem", sm: "0.875rem" },
              }}
            >
              {comment.username}
            </Typography>
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ 
                ml: { xs: 0.75, sm: 1 },
                fontSize: { xs: "0.75rem", sm: "0.8125rem" },
              }}
            >
              {new Date(comment.create_time).toLocaleDateString()}
            </Typography>
          </Box>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              lineHeight: { xs: 1.5, sm: 1.6 },
              mb: { xs: 0.75, sm: 1 },
              fontSize: { xs: "0.8125rem", sm: "0.875rem" },
              wordWrap: "break-word",
            }}
          >
            {comment.comment}
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: { xs: 0.5, sm: 1 },
            flexWrap: "wrap",
          }}>
            <IconButton
              size={isMobile ? "medium" : "small"}
              onClick={handleLike}
              disabled={isLiking || !authorized}
              sx={{
                minWidth: { xs: "44px", sm: "40px" },
                minHeight: { xs: "44px", sm: "40px" },
                color: isLiked ? "error.main" : "text.secondary",
                '&:hover': {
                  color: "error.main",
                  backgroundColor: "rgba(211, 47, 47, 0.1)",
                },
              }}
            >
              {isLiked ? (
                <FavoriteIcon fontSize={isMobile ? "medium" : "small"} />
              ) : (
                <FavoriteBorderIcon fontSize={isMobile ? "medium" : "small"} />
              )}
            </IconButton>
            {likeCount > 0 && (
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                  minWidth: "20px",
                }}
              >
                {likeCount}
              </Typography>
            )}
            {authorized && level < maxLevel && (
              <>
                <IconButton
                  size={isMobile ? "medium" : "small"}
                  onClick={handleReplyClick}
                  sx={{
                    color: 'text.secondary',
                    minWidth: { xs: "44px", sm: "40px" },
                    minHeight: { xs: "44px", sm: "40px" },
                    '&:hover': {
                      color: 'primary.main',
                      backgroundColor: 'rgba(18, 141, 111, 0.1)',
                    },
                    '&:active': {
                      backgroundColor: 'rgba(18, 141, 111, 0.2)',
                    },
                  }}
                >
                  <ReplyIcon fontSize={isMobile ? "medium" : "small"} />
                </IconButton>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ 
                    cursor: 'pointer',
                    fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                    minHeight: { xs: "44px", sm: "auto" },
                    display: "flex",
                    alignItems: "center",
                    '&:hover': { color: 'primary.main' },
                    '&:active': { color: 'primary.dark' },
                  }}
                  onClick={handleReplyClick}
                >
                  Reply
                </Typography>
              </>
            )}
            
            {canDelete && onDeleteComment && (
              <>
                <IconButton
                  size={isMobile ? "medium" : "small"}
                  onClick={() => onDeleteComment(comment.id)}
                  sx={{
                    color: 'error.main',
                    minWidth: { xs: "44px", sm: "40px" },
                    minHeight: { xs: "44px", sm: "40px" },
                    '&:hover': {
                      color: 'error.dark',
                      backgroundColor: 'rgba(211, 47, 47, 0.1)',
                    },
                    '&:active': {
                      backgroundColor: 'rgba(211, 47, 47, 0.2)',
                    },
                  }}
                >
                  <DeleteIcon fontSize={isMobile ? "medium" : "small"} />
                </IconButton>
              </>
            )}
            
            {level < maxLevel && (
              <>
                <IconButton
                  size={isMobile ? "medium" : "small"}
                  onClick={() => onToggleReplies(comment)}
                  sx={{
                    ml: { xs: 0, sm: 1 },
                    minWidth: { xs: "44px", sm: "40px" },
                    minHeight: { xs: "44px", sm: "40px" },
                    color: expandedReplies[comment.id] ? "primary.main" : "text.secondary",
                    "&:hover": {
                      backgroundColor: "rgba(18, 141, 111, 0.1)",
                    },
                    "&:active": {
                      backgroundColor: "rgba(18, 141, 111, 0.2)",
                    },
                  }}
                >
                  <CommentIcon fontSize={isMobile ? "medium" : "small"} />
                </IconButton>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ 
                    cursor: 'pointer',
                    fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                    minHeight: { xs: "44px", sm: "auto" },
                    display: "flex",
                    alignItems: "center",
                    '&:hover': { color: 'primary.main' },
                    '&:active': { color: 'primary.dark' },
                  }}
                  onClick={() => onToggleReplies(comment)}
                >
                  Replies
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>

      <Collapse in={showReplyInput}>
        <Box sx={{ 
          mt: { xs: 1.5, sm: 2 }, 
          ml: { xs: 2, sm: 3, md: 4 },
        }}>
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 0.75, sm: 1 }, 
            alignItems: 'flex-start' 
          }}>
            <TextField
              size={isMobile ? "small" : "small"}
              fullWidth
              placeholder="Write a reply..."
              value={newReply[comment.id] || ""}
              onChange={(e) => onReplyChange(comment.id, e.target.value)}
              disabled={submitting[comment.id] || !authorized}
              multiline
              rows={isMobile ? 2 : 1}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                  fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                  '&:hover': {
                    backgroundColor: 'background.paper',
                  },
                },
              }}
            />
            <IconButton
              onClick={handleSubmitReply}
              disabled={!newReply[comment.id]?.trim() || submitting[comment.id] || !authorized}
              color="primary"
              size={isMobile ? "medium" : "small"}
              sx={{
                mt: { xs: 0.25, sm: 0.5 },
                minWidth: { xs: "44px", sm: "40px" },
                minHeight: { xs: "44px", sm: "40px" },
                '&.Mui-disabled': {
                  color: 'rgba(0, 0, 0, 0.26)',
                },
                '&:active': {
                  backgroundColor: 'rgba(18, 141, 111, 0.2)',
                },
              }}
            >
              <SendIcon fontSize={isMobile ? "medium" : "small"} />
            </IconButton>
          </Box>
        </Box>
      </Collapse>

      <Collapse in={expandedReplies[comment.id]}>
        <Box sx={{ 
          mt: { xs: 1.5, sm: 2 }, 
          ml: { xs: 2, sm: 3, md: 4 },
        }}>
          {comment.replies && comment.replies.length > 0 ? (
            comment.replies.map((reply) => (
              <RateComment
                key={reply.id}
                comment={reply}
                level={level + 1}
                onReply={onReply}
                authorized={authorized}
                submitting={submitting}
                newReply={newReply}
                onReplyChange={onReplyChange}
                onSubmitReply={onSubmitReply}
                onToggleReplies={onToggleReplies}
                expandedReplies={expandedReplies}
                onDeleteComment={onDeleteComment}
                currentUser={currentUser}
                isSpaceOwner={isSpaceOwner}
              />
            ))
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, fontStyle: "italic" }}
            >
              No replies yet
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default RateComment; 